import React from "react";
import {
  NotificationItem,
  NotificationItemColors,
} from "../notification-item/notification-item";
import "./notification-list.scss";

export interface NotificationListEntry {
  /** Unique identifier for the notification. */
  id: string;
  /** Notification title. */
  title: string;
  /** Relative or absolute timestamp. */
  timestamp: string;
}

/** Default color tokens for the notification list container. */
export const NOTIFICATION_LIST_DEFAULT_COLORS = {
  /** Outer container border. */
  borderColor: "#d6d6d6",
  /** Divider between items. */
  dividerColor: "#d6d6d6",
  /** Container background. */
  backgroundColor: "#ffffff",
} as const;

export interface NotificationListColors {
  borderColor?: string;
  dividerColor?: string;
  backgroundColor?: string;
}

export interface NotificationListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "onSelect"> {
  /** Notifications to render in the list. */
  notifications: NotificationListEntry[];
  /** ID of the currently selected notification. */
  selectedId?: string;
  /** Called when a notification is selected. */
  onSelect?: (id: string) => void;
  /** When true, truncates notification titles with an ellipsis. Defaults to true. */
  truncateTitle?: boolean;
  /** Color overrides for individual notification items. */
  itemColors?: NotificationItemColors;
  /** Color overrides for the list container. */
  colors?: NotificationListColors;
  /** Optional max height; enables vertical scrolling when content overflows. */
  maxHeight?: number | string;
  /** Content shown when the notifications array is empty. */
  emptyContent?: React.ReactNode;
  /** Additional CSS class name for the root element. */
  className?: string;
}

const defaultColors = NOTIFICATION_LIST_DEFAULT_COLORS;

export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  selectedId,
  onSelect,
  truncateTitle = true,
  itemColors,
  colors = {},
  maxHeight,
  emptyContent = "No notifications",
  className = "",
  style,
  ...rest
}) => {
  const borderColor = colors.borderColor ?? defaultColors.borderColor;
  const dividerColor = colors.dividerColor ?? defaultColors.dividerColor;
  const backgroundColor = colors.backgroundColor ?? defaultColors.backgroundColor;

  const cssVarStyle: React.CSSProperties = {
    ["--notification-list-border" as string]: borderColor,
    ["--notification-list-divider" as string]: dividerColor,
    ["--notification-list-bg" as string]: backgroundColor,
    ...(maxHeight != null
      ? { maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight }
      : {}),
    ...style,
  };

  const isSelectable = typeof onSelect === "function";

  return (
    <div
      className={[
        "notification-list",
        maxHeight != null ? "notification-list--scrollable" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={cssVarStyle}
      role="list"
      data-name="NotificationList"
      {...rest}
    >
      {notifications.length === 0 ? (
        <div className="notification-list__empty">{emptyContent}</div>
      ) : (
        notifications.map((notification) => (
          <div
            key={notification.id}
            className="notification-list__item"
            role="listitem"
          >
            <NotificationItem
              title={notification.title}
              timestamp={notification.timestamp}
              selected={selectedId === notification.id}
              truncateTitle={truncateTitle}
              colors={itemColors}
              onClick={
                isSelectable ? () => onSelect(notification.id) : undefined
              }
            />
          </div>
        ))
      )}
    </div>
  );
};

export default NotificationList;
